// src/components/BookingForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './BookingForm.css';

/* ---------- Static data ------------------------------------------------ */

const facilities = [
  {
    value: 'Taylorstown Pitch',
    label: 'Taylorstown',
    sub: 'Full pitch · grass',
    key: 'taylorstown',
    kind: 'pitch',
  },
  {
    value: 'Loughbeg Road Pitch',
    label: 'Loughbeg Rd',
    sub: 'Full pitch · grass',
    key: 'loughbeg',
    kind: 'pitch',
  },
  {
    value: 'Gym',
    label: 'Gym',
    sub: 'Indoor · strength',
    key: 'gym',
    kind: 'gym',
  },
];

const sessionTypes = [
  { id: 'Training', name: 'Training' },
  { id: 'Match',    name: 'Match' },
  { id: 'Maintenance', name: 'Maintenance' },
  { id: 'Event',    name: 'Event' },
];

/* ---------- Helpers ---------------------------------------------------- */

const calcDuration = (start, end) => {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const minutes = (eh * 60 + em) - (sh * 60 + sm);
  if (minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const formatDateLong = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
};

/* ---------- SVG icons -------------------------------------------------- */

/* Gaelic football pitch — portrait.
   Endlines at top/bottom, H-posts extending beyond the field,
   13m small rectangle, 13/20/45m lines across the full width,
   D arc from the 20m line, halfway line with centre spot. */
const PitchIcon = ({ highlight = 'none' }) => (
  <svg viewBox="0 0 120 180" className="pitch-svg" aria-hidden="true">
    {/* Field base (always vibrant green — stroke has consistent contrast) */}
    <rect x="8" y="12" width="104" height="156" rx="2"
          className="pitch-base" />

    {/* When showing "half", dim the bottom half so the top reads as selected */}
    {highlight === 'half' && (
      <rect x="8" y="90" width="104" height="78"
            className="pitch-dim" />
    )}

    {/* ==== TOP END ==== */}
    {/* H-shaped goalposts extending above the endline */}
    <line x1="56" y1="12" x2="56" y2="3" className="pitch-post" />
    <line x1="64" y1="12" x2="64" y2="3" className="pitch-post" />
    <line x1="54" y1="7" x2="66" y2="7" className="pitch-post" />

    {/* Small rectangle (in front of goal) */}
    <rect x="53" y="12" width="14" height="5" fill="none" className="pitch-line" />

    {/* 13m line across */}
    <line x1="8" y1="26" x2="112" y2="26" className="pitch-line" />

    {/* 20m line across */}
    <line x1="8" y1="34" x2="112" y2="34" className="pitch-line" />

    {/* Scoring arc (D) bulging into the field from the 20m line */}
    <path d="M 52 34 A 8 8 0 0 1 68 34" fill="none" className="pitch-line" />

    {/* 45m line */}
    <line x1="8" y1="60" x2="112" y2="60" className="pitch-line" />

    {/* ==== HALFWAY ==== */}
    <line x1="8" y1="90" x2="112" y2="90" className="pitch-line" />
    <circle cx="60" cy="90" r="1.4" className="pitch-dot" />

    {/* ==== BOTTOM END (mirrored) ==== */}
    {/* 45m */}
    <line x1="8" y1="120" x2="112" y2="120" className="pitch-line" />

    {/* 20m */}
    <line x1="8" y1="146" x2="112" y2="146" className="pitch-line" />

    {/* Bottom D */}
    <path d="M 52 146 A 8 8 0 0 0 68 146" fill="none" className="pitch-line" />

    {/* 13m */}
    <line x1="8" y1="154" x2="112" y2="154" className="pitch-line" />

    {/* Small rectangle */}
    <rect x="53" y="163" width="14" height="5" fill="none" className="pitch-line" />

    {/* H-posts */}
    <line x1="56" y1="168" x2="56" y2="177" className="pitch-post" />
    <line x1="64" y1="168" x2="64" y2="177" className="pitch-post" />
    <line x1="54" y1="173" x2="66" y2="173" className="pitch-post" />
  </svg>
);

/* Mini Gaelic pitch mark for facility tiles */
const PitchMark = () => (
  <svg width="22" height="28" viewBox="0 0 24 32" fill="none"
       stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
       strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="3.5" width="17" height="25" rx="1.5" />
    <line x1="3.5" y1="16" x2="20.5" y2="16" />
    <circle cx="12" cy="16" r="0.9" fill="currentColor" stroke="none" />
    <rect x="9" y="3.5" width="6" height="2.2" />
    <rect x="9" y="26.3" width="6" height="2.2" />
    <path d="M 10 6 A 2 2 0 0 1 14 6" />
    <path d="M 10 26 A 2 2 0 0 0 14 26" />
  </svg>
);

/* Bold dumbbell logo mark for the gym */
const DumbbellMark = () => (
  <svg width="30" height="18" viewBox="0 0 32 20" fill="currentColor" aria-hidden="true">
    {/* Outer end-caps */}
    <rect x="1"  y="6" width="2.6" height="8"  rx="0.8" />
    <rect x="28.4" y="6" width="2.6" height="8" rx="0.8" />
    {/* Inner weight plates */}
    <rect x="4.6" y="2" width="4" height="16" rx="1.2" />
    <rect x="23.4" y="2" width="4" height="16" rx="1.2" />
    {/* Grip bar */}
    <rect x="8.6" y="8" width="14.8" height="4" rx="0.9" />
  </svg>
);

/* ---------- Component -------------------------------------------------- */

const BookingForm = ({ onAddBooking }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    date: '',
    startTime: '',
    endTime: '',
    pitch: '',
    pitchPortion: 'full',
    sessionType: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* Derived values */
  const duration     = useMemo(() => calcDuration(formData.startTime, formData.endTime), [formData.startTime, formData.endTime]);
  const dateLabel    = useMemo(() => formatDateLong(formData.date), [formData.date]);
  const isPitch      = formData.pitch && formData.pitch !== 'Gym';
  const requiredFields = ['pitch', 'date', 'startTime', 'endTime', 'sessionType'];
  const progress     = Math.round((requiredFields.filter(f => formData[f]).length / requiredFields.length) * 100);

  /* Generic change handler for native inputs */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    if (formErrors[name]) setFormErrors((err) => ({ ...err, [name]: '' }));
  };

  /* Direct setter for tile/pill selections */
  const setField = (name, value) => {
    setFormData((s) => ({ ...s, [name]: value }));
    if (formErrors[name]) setFormErrors((err) => ({ ...err, [name]: '' }));
  };

  const handleFacility = (value) => {
    setFormData((s) => ({
      ...s,
      pitch: value,
      pitchPortion: value === 'Gym' ? 'full' : s.pitchPortion,
    }));
    if (formErrors.pitch) setFormErrors((err) => ({ ...err, pitch: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.pitch) errors.pitch = 'Pick a facility.';
    if (!formData.date)  errors.date  = 'Pick a date.';
    if (!formData.startTime) errors.startTime = 'Start time required.';
    if (!formData.endTime)   errors.endTime   = 'End time required.';
    if (formData.startTime && formData.endTime) {
      const s = new Date(`2023-01-01T${formData.startTime}`);
      const e = new Date(`2023-01-01T${formData.endTime}`);
      if (e <= s) errors.endTime = 'End time must be after start.';
    }
    if (!formData.sessionType) errors.sessionType = 'Pick a session type.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const result = await onAddBooking(formData);
      // Backwards-compatible: handler now returns { ok, error } but old call
      // sites returned a bare boolean.
      const ok = result === true || result?.ok === true;
      if (ok) {
        navigate('/');
      } else {
        const message =
          (result && result.error) ||
          'Failed to add booking. Please try again.';
        setFormErrors({ submit: message });
      }
    } catch (err) {
      console.error('Error submitting booking:', err);
      setFormErrors({
        submit: `An error occurred: ${err?.message || 'unknown error'}.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* -------- Render ---------------------------------------------------- */

  return (
    <div className="bf-page">
      <div className="bf-topo" aria-hidden="true" />

      <div className="bf-shell">
        <form onSubmit={handleSubmit} className="bf-form" noValidate>

          <header className="bf-header">
            <div className="bf-eyebrow">
              <span className="bf-dot" /> New booking
            </div>
            <h1 className="bf-title">
              Lock in a <em>session</em>.
            </h1>
            <p className="bf-sub">
              Build the brief step by step. The sheet on the right
              fills itself in as you go.
            </p>
          </header>

          {formErrors.submit && (
            <div className="bf-alert" role="alert">
              <span>!</span> {formErrors.submit}
            </div>
          )}

          {/* ---------- 01 Facility ---------- */}
          <section className="bf-section" style={{ '--d': '0ms' }}>
            <div className="bf-section-head">
              <span className="bf-num">01</span>
              <h2 className="bf-section-title">Facility</h2>
              <span className="bf-section-rule" />
            </div>

            <div className="bf-tiles">
              {facilities.map((f) => (
                <button
                  type="button"
                  key={f.value}
                  onClick={() => handleFacility(f.value)}
                  className={`bf-tile ${formData.pitch === f.value ? 'is-active' : ''}`}
                  aria-pressed={formData.pitch === f.value}
                >
                  <span className={`bf-tile-icon bf-tile-icon--${f.key}`} aria-hidden="true">
                    {f.kind === 'gym' ? <DumbbellMark /> : <PitchMark />}
                  </span>
                  <span className="bf-tile-body">
                    <span className="bf-tile-label">{f.label}</span>
                    <span className="bf-tile-sub">{f.sub}</span>
                  </span>
                  <span className="bf-tile-check" aria-hidden="true">✓</span>
                </button>
              ))}
            </div>
            {formErrors.pitch && <p className="bf-err">{formErrors.pitch}</p>}

            {isPitch && (
              <div className="bf-subsection">
                <div className="bf-subsection-label">Pitch portion</div>
                <div className="bf-portion-grid">
                  {['full', 'half'].map((p) => (
                    <button
                      type="button"
                      key={p}
                      onClick={() => setField('pitchPortion', p)}
                      className={`bf-portion ${formData.pitchPortion === p ? 'is-active' : ''}`}
                      aria-pressed={formData.pitchPortion === p}
                    >
                      <PitchIcon highlight={p} />
                      <span className="bf-portion-label">
                        {p === 'full' ? 'Full pitch' : 'Half pitch'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* ---------- 02 When ---------- */}
          <section className="bf-section" style={{ '--d': '60ms' }}>
            <div className="bf-section-head">
              <span className="bf-num">02</span>
              <h2 className="bf-section-title">When</h2>
              <span className="bf-section-rule" />
            </div>

            <div className="bf-field">
              <label htmlFor="date" className="bf-label">
                <span className="bf-label-text">Date<span className="bf-req">*</span></span>
                {dateLabel && <span className="bf-label-hint">{dateLabel}</span>}
              </label>
              <input
                id="date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                className={`bf-input ${formErrors.date ? 'is-error' : ''}`}
              />
              {formErrors.date && <p className="bf-err">{formErrors.date}</p>}
            </div>

            <div className="bf-row-2">
              <div className="bf-field">
                <label htmlFor="startTime" className="bf-label">
                  <span className="bf-label-text">Start<span className="bf-req">*</span></span>
                </label>
                <input
                  id="startTime"
                  name="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={handleChange}
                  className={`bf-input ${formErrors.startTime ? 'is-error' : ''}`}
                />
                {formErrors.startTime && <p className="bf-err">{formErrors.startTime}</p>}
              </div>

              <div className="bf-field">
                <label htmlFor="endTime" className="bf-label">
                  <span className="bf-label-text">End<span className="bf-req">*</span></span>
                  {duration && <span className="bf-label-hint">{duration}</span>}
                </label>
                <input
                  id="endTime"
                  name="endTime"
                  type="time"
                  value={formData.endTime}
                  onChange={handleChange}
                  className={`bf-input ${formErrors.endTime ? 'is-error' : ''}`}
                />
                {formErrors.endTime && <p className="bf-err">{formErrors.endTime}</p>}
              </div>
            </div>
          </section>

          {/* ---------- 03 Session type ---------- */}
          <section className="bf-section" style={{ '--d': '120ms' }}>
            <div className="bf-section-head">
              <span className="bf-num">03</span>
              <h2 className="bf-section-title">Session type</h2>
              <span className="bf-section-rule" />
            </div>

            <div className="bf-pills">
              {sessionTypes.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => setField('sessionType', t.id)}
                  className={`bf-pill ${formData.sessionType === t.id ? 'is-active' : ''}`}
                  aria-pressed={formData.sessionType === t.id}
                >
                  {t.name}
                </button>
              ))}
            </div>
            {formErrors.sessionType && <p className="bf-err">{formErrors.sessionType}</p>}
          </section>

          {/* ---------- 04 Notes ---------- */}
          <section className="bf-section" style={{ '--d': '180ms' }}>
            <div className="bf-section-head">
              <span className="bf-num">04</span>
              <h2 className="bf-section-title">Notes</h2>
              <span className="bf-section-rule" />
              <span className="bf-optional">optional</span>
            </div>

            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              placeholder="Drills, opposition, squad details, kit colour…"
              className="bf-textarea"
            />

            <div className="bf-note">
              Multiple bookings can share a time slot — half each, shared pitch,
              overlapping drills. Go for it.
            </div>
          </section>

          {/* ---------- Match brief scorecard ---------- */}
          <section className="bf-scorecard" aria-label="Booking brief" style={{ '--d': '240ms' }}>
            <div className="bf-scorecard-head">
              <span className="bf-scorecard-eyebrow">Match brief</span>
              <div className="bf-scorecard-meta">
                <span className="bf-scorecard-status">
                  {progress === 100 ? 'Ready to submit' : 'In progress'}
                </span>
                <span className="bf-scorecard-pct">
                  {progress}<small>%</small>
                </span>
              </div>
            </div>
            <div className="bf-scorecard-bar">
              <div className="bf-scorecard-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <dl className="bf-scorecard-grid">
              <ScoreCell label="Facility" value={formData.pitch} />
              <ScoreCell
                label="Portion"
                value={
                  isPitch
                    ? (formData.pitchPortion === 'half' ? 'Half pitch' : 'Full pitch')
                    : (formData.pitch === 'Gym' ? 'Indoor' : null)
                }
              />
              <ScoreCell label="Date" value={dateLabel} />
              <ScoreCell
                label="Time"
                modifier="time"
                value={
                  formData.startTime && formData.endTime
                    ? `${formData.startTime}–${formData.endTime}`
                    : null
                }
              />
              <ScoreCell label="Duration" value={duration} />
              <ScoreCell label="Session" value={formData.sessionType} />
            </dl>
          </section>

          {/* ---------- Actions ---------- */}
          <div className="bf-actions">
            <button type="button" onClick={() => navigate('/')} className="bf-btn bf-btn-ghost">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="bf-btn bf-btn-primary">
              {isSubmitting ? (
                <>
                  <span className="bf-spinner" /> Creating…
                </>
              ) : (
                <>
                  Create booking
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" /><path d="M13 6l6 6-6 6" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const ScoreCell = ({ label, value, modifier }) => (
  <div className={`bf-score-cell ${modifier ? `bf-score-cell--${modifier}` : ''} ${value ? 'has-val' : ''}`}>
    <dt>{label}</dt>
    <dd>{value || <span className="bf-score-empty">—</span>}</dd>
  </div>
);

export default BookingForm;
